import styled from '@emotion/styled';
import React, { useCallback } from 'react';
import { useDeviceVideoUrls } from '../../../utils/hooks/useDeviceVideo';
import { breakpoints } from '../../../utils/styleguide';
import useIsVisible from '../../../utils/useIsVisible';
import AspectRatioBox from '../layout/AspectRatioBox';
import VideoPlayer from '../video/FullScreenVideoPlayer';

const Container = styled(AspectRatioBox)`
  ${breakpoints.tablet} {
    width: auto;
  }
`;

const StackingContext = styled.div`
  position: relative;
  z-index: 0;
  width: 100%;
  height: 100%;
`;

const Overlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  opacity: 0.3;
  background-color: black;
`;

const ContentWrapper = styled.div<{ passThroughPointerEvents: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  ${({ passThroughPointerEvents }) => passThroughPointerEvents && 'pointer-events: none;'}
`;

const defaultAspectRatios = {
  mobile: 4 / 5,
  tablet: 16 / 9,
  desktop: 16 / 9,
};

type Props = {
  videoLargeScreenUrl?: string;
  videoSmallScreenUrl?: string;
  posterLargeScreenUrl?: string;
  posterSmallScreenUrl?: string;
  children?: React.ReactNode;
  hlsConfig?: Partial<Hls.Config>;
  aspectRatios?: Partial<typeof defaultAspectRatios>;
  hideMute?: boolean;
  passThroughPointerEvents?: boolean;
  showControls?: boolean;
  hasOverlay?: boolean;
};

const BackgroundVideo = ({
  videoLargeScreenUrl = '',
  videoSmallScreenUrl = '',
  posterLargeScreenUrl = '',
  posterSmallScreenUrl = '',
  children,
  hlsConfig,
  aspectRatios,
  hideMute = true,
  passThroughPointerEvents = false,
  showControls = true,
  hasOverlay,
}: Props) => {
  const { videoUrl: url, posterUrl } = useDeviceVideoUrls({
    largeScreen: {
      videoUrl: videoLargeScreenUrl,
      posterUrl: posterLargeScreenUrl,
    },
    smallScreen: {
      videoUrl: videoSmallScreenUrl,
      posterUrl: posterSmallScreenUrl,
    },
  });
  const [isVisible, setVisible] = React.useState(true);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  useIsVisible({
    ref: containerRef,
    onIntersecting: useCallback(({ isIntersecting }) => {
      setVisible(isIntersecting);
    }, []),
  });

  return (
    <Container
      responsiveAspectRatios={{ ...defaultAspectRatios, ...aspectRatios }}
      boxRef={containerRef}
    >
      <StackingContext>
        <VideoPlayer
          src={url}
          shouldPlay={isVisible}
          hideMute={hideMute}
          hlsConfig={hlsConfig}
          posterSrc={posterUrl}
          showControls={showControls}
        />
      </StackingContext>
      {hasOverlay && <Overlay />}
      {children && (
        <ContentWrapper passThroughPointerEvents={passThroughPointerEvents}>
          {children}
        </ContentWrapper>
      )}
    </Container>
  );
};

export default BackgroundVideo;
